import { useLocation } from 'react-router-dom';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { AlertCircle, Download, CreditCard, Eye, Calendar } from 'lucide-react';

/**
 * ReadOnlyModeGate
 * 
 * For suspended/pending_deletion companies:
 * - Company Admins get READ-ONLY access to ALL pages
 * - Full functional access to Billing only
 * - Can request data export
 * - Cannot create/edit/delete anything except billing operations
 * 
 * Access Model:
 * - suspended: Read-only site + Full billing (to fix payment)
 * - pending_deletion: Read-only site + Full billing + Data export (countdown to deletion)
 */

// Routes that have FULL functional access even in read-only mode
const FULL_ACCESS_ROUTES = [
  '/billing',
  '/logout',
  '/account-unavailable',
  '/profile'
];

/**
 * Check if user is in read-only mode
 */
export function isReadOnlyMode(user) {
  return user?.read_only_mode === true;
}

/**
 * Check if current route has full access in read-only mode
 */
export function hasFullAccess(pathname) {
  return FULL_ACCESS_ROUTES.some(route => 
    pathname === route || pathname.startsWith(route + '/')
  );
}

/**
 * ReadOnlyBanner
 * 
 * Persistent banner shown when in read-only mode.
 * Shows different messages for suspended vs pending_deletion.
 */
export function ReadOnlyBanner({ user }) {
  const location = useLocation();
  
  // Don't show if not in read-only mode
  if (!isReadOnlyMode(user)) {
    return null;
  }
  
  // Don't show on billing page - it has its own warnings
  if (location.pathname === '/billing' || location.pathname.startsWith('/billing/')) {
    return null;
  }
  
  const isPendingDeletion = user?.company_status === 'pending_deletion';
  const deletionDate = user?.deletion_scheduled_at;
  
  // Calculate days until deletion
  let daysUntilDeletion = null;
  if (deletionDate) {
    const deletion = new Date(deletionDate);
    const now = new Date();
    daysUntilDeletion = Math.ceil((deletion - now) / (1000 * 60 * 60 * 24));
  }
  
  if (isPendingDeletion) {
    return (
      <div className="bg-red-50 border-b border-red-200 px-4 py-3" data-testid="read-only-banner-deletion">
        <div className="max-w-7xl mx-auto flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-red-100 rounded-full shrink-0">
              <Calendar className="h-5 w-5 text-red-600" />
            </div>
            <div>
              <p className="text-sm font-medium text-red-800">
                Account Scheduled for Deletion
              </p>
              <p className="text-xs text-red-600">
                {daysUntilDeletion !== null 
                  ? `${daysUntilDeletion} day${daysUntilDeletion !== 1 ? 's' : ''} remaining. `
                  : ''
                }
                Read-only access. Export your data before deletion.
              </p>
            </div>
          </div>
          <div className="flex gap-2 shrink-0">
            <a 
              href="/billing?tab=export"
              className="flex items-center gap-2 px-4 py-2 bg-gray-600 text-white text-sm font-medium rounded-lg hover:bg-gray-700 transition-colors"
              data-testid="export-data-btn"
            >
              <Download className="h-4 w-4" />
              Export Data
            </a>
            <a 
              href="/billing"
              className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white text-sm font-medium rounded-lg hover:bg-red-700 transition-colors"
              data-testid="reactivate-btn"
            >
              <CreditCard className="h-4 w-4" />
              Reactivate
            </a>
          </div>
        </div>
      </div>
    );
  }
  
  // Suspended status
  return (
    <div className="bg-amber-50 border-b border-amber-200 px-4 py-3" data-testid="read-only-banner-suspended">
      <div className="max-w-7xl mx-auto flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-amber-100 rounded-full shrink-0">
            <Eye className="h-5 w-5 text-amber-600" />
          </div>
          <div>
            <p className="text-sm font-medium text-amber-800">
              Read-Only Mode
            </p>
            <p className="text-xs text-amber-600">
              Your organization has a billing issue. You can view all data but cannot make changes.
            </p>
          </div>
        </div>
        <a 
          href="/billing"
          className="flex items-center gap-2 px-4 py-2 bg-amber-600 text-white text-sm font-medium rounded-lg hover:bg-amber-700 transition-colors shrink-0"
          data-testid="resolve-billing-btn"
        >
          <CreditCard className="h-4 w-4" />
          Resolve Billing Issue
        </a>
      </div>
    </div>
  );
}

/**
 * ReadOnlyActionBlocker
 * 
 * Wraps action buttons/forms and disables them in read-only mode.
 * Shows tooltip explaining why action is blocked.
 */
export function ReadOnlyActionBlocker({ 
  children, 
  user, 
  allowedActions = [],
  className = '' 
}) {
  const location = useLocation();
  
  // Not in read-only mode - allow everything
  if (!isReadOnlyMode(user)) {
    return children;
  }
  
  // Check if current route has full access
  if (hasFullAccess(location.pathname)) {
    return children;
  }
  
  // Render children with disabled state
  return (
    <div className={`relative ${className}`} data-read-only="true">
      <div className="opacity-50 pointer-events-none">
        {children}
      </div>
      <div className="absolute inset-0 flex items-center justify-center bg-gray-100/50 rounded cursor-not-allowed">
        <span className="text-xs text-gray-500 bg-white px-2 py-1 rounded shadow-sm">
          Read-only mode
        </span>
      </div>
    </div>
  );
}

/**
 * Hook to check if actions are allowed
 */
export function useReadOnlyMode(user) {
  const location = useLocation();
  
  const isReadOnly = isReadOnlyMode(user);
  const canPerformAction = !isReadOnly || hasFullAccess(location.pathname);
  const isPendingDeletion = user?.company_status === 'pending_deletion';
  const deletionDate = user?.deletion_scheduled_at ? new Date(user.deletion_scheduled_at) : null;
  
  return {
    isReadOnly,
    canPerformAction,
    isPendingDeletion,
    deletionDate,
    companyStatus: user?.company_status
  };
}

export default ReadOnlyBanner;
